import { useState, useEffect, useCallback } from 'react';
import {
  getLLMChains,
  getLLMProviders,
  getVoiceConfig,
  getLLMUsage,
  getSystemPrompts,
} from '../services/adminApiClient';
import type {
  LLMChain,
  LLMProvider,
  VoiceConfig,
  LLMUsage,
  SystemPrompt,
} from '../types';

const TABS = ['Model Chains', 'LLM Providers', 'Voice Config', 'Usage', 'System Prompts'] as const;
type Tab = typeof TABS[number];

const PROVIDER_COLORS: Record<string, string> = {
  openrouter: 'bg-purple-100 text-purple-700',
  gemini:     'bg-blue-100 text-blue-700',
  openai:     'bg-green-100 text-green-700',
  anthropic:  'bg-amber-100 text-amber-700',
};

const VOICE_COLORS: Record<string, string> = {
  elevenlabs: 'bg-violet-100 text-violet-700',
  deepgram:   'bg-sky-100 text-sky-700',
  sarvam:     'bg-orange-100 text-orange-700',
};

// ─── Model Chains Tab ─────────────────────────────────────────────────────────

function ChainsTab() {
  const [chains, setChains] = useState<LLMChain[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getLLMChains()
      .then(r => setChains(r.data.chains))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner />;

  return (
    <div>
      <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
        <svg className="mt-0.5 h-4 w-4 shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
        </svg>
        <span>Model chains are read-only. To change a chain, edit <code className="font-mono">.env</code> and restart the AI Agents container.</span>
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3 text-left">Chain</th>
              <th className="px-4 py-3 text-left">Env Key</th>
              <th className="px-4 py-3 text-left">Fallback Order</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {chains.map(chain => (
              <tr key={chain.key} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-medium text-slate-800">{chain.label}</td>
                <td className="px-4 py-3 font-mono text-xs text-slate-500">{chain.key}</td>
                <td className="px-4 py-3">
                  {chain.models.length === 0 ? (
                    <span className="text-slate-400 italic">not set</span>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {chain.models.map((m, i) => (
                        <span key={i} className="flex items-center gap-1">
                          {i > 0 && <span className="text-slate-300 text-xs">→</span>}
                          <span className={`rounded px-2 py-0.5 text-xs font-medium ${PROVIDER_COLORS[m.provider] ?? 'bg-slate-100 text-slate-600'}`}>
                            {m.provider}/<span className="opacity-75">{m.model}</span>
                          </span>
                        </span>
                      ))}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── LLM Providers Tab ────────────────────────────────────────────────────────

function ProvidersTab() {
  const [providers, setProviders] = useState<LLMProvider[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getLLMProviders()
      .then(r => setProviders(r.data.providers))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner />;

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
      {providers.map(p => (
        <div key={p.name} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <span className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${PROVIDER_COLORS[p.name] ?? 'bg-slate-100 text-slate-600'}`}>
              {p.name}
            </span>
            {p.configured ? (
              <span className="flex items-center gap-1 text-xs font-medium text-emerald-600">
                <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" /></svg>
                Configured
              </span>
            ) : (
              <span className="flex items-center gap-1 text-xs font-medium text-red-500">
                <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" /></svg>
                Missing
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500 mb-1 font-mono">{p.env_key}</p>
          {p.configured && p.key_preview && (
            <p className="text-xs text-slate-400 mb-3 font-mono">Key: {p.key_preview}</p>
          )}
          <div className="mt-3 border-t border-slate-100 pt-3">
            <p className="text-sm text-slate-700">
              <span className="font-semibold text-slate-900">{p.chain_usage_count}</span>{' '}
              chain slot{p.chain_usage_count !== 1 ? 's' : ''} use this provider
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Voice Config Tab ─────────────────────────────────────────────────────────

function VoiceConfigTab() {
  const [config, setConfig] = useState<VoiceConfig | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getVoiceConfig()
      .then(r => setConfig(r.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner />;
  if (!config) return null;

  const voiceProviders = [
    { key: 'elevenlabs', label: 'ElevenLabs', role: 'TTS', languages: 'English + 9 world languages', model: 'eleven_multilingual_v2' },
    { key: 'deepgram',   label: 'Deepgram',   role: 'STT', languages: 'English + major world languages', model: 'nova-2' },
    { key: 'sarvam',     label: 'Sarvam AI',  role: 'STT + TTS', languages: 'Bengali, Tamil, Telugu, Marathi, Kannada', model: 'saaras:v3 / bulbul:v3' },
  ];

  return (
    <div className="space-y-6">
      {/* API Key Status */}
      <div>
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Provider API Keys</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {voiceProviders.map(vp => {
            const keyInfo = config.provider_keys[vp.key];
            return (
              <div key={vp.key} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${VOICE_COLORS[vp.key] ?? 'bg-slate-100 text-slate-600'}`}>
                    {vp.label}
                  </span>
                  {keyInfo?.configured ? (
                    <span className="flex items-center gap-1 text-xs font-medium text-emerald-600">
                      <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" /></svg>
                      Configured
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-xs font-medium text-red-500">
                      <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" /></svg>
                      Missing
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-500 mb-0.5"><span className="font-medium text-slate-700">Role:</span> {vp.role}</p>
                <p className="text-xs text-slate-500 mb-0.5"><span className="font-medium text-slate-700">Model:</span> {vp.model}</p>
                <p className="text-xs text-slate-500"><span className="font-medium text-slate-700">Languages:</span> {vp.languages}</p>
                {keyInfo?.configured && keyInfo.key_preview && (
                  <p className="mt-2 text-xs font-mono text-slate-400">Key: {keyInfo.key_preview}</p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Language Routing Table */}
      <div>
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Language Routing</h3>
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">Language</th>
                <th className="px-4 py-3 text-left">Code</th>
                <th className="px-4 py-3 text-left">STT Provider</th>
                <th className="px-4 py-3 text-left">STT Model</th>
                <th className="px-4 py-3 text-left">TTS Provider</th>
                <th className="px-4 py-3 text-left">TTS Model</th>
                <th className="px-4 py-3 text-left">Voice</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {config.language_routing.map(lang => (
                <tr key={lang.code} className="hover:bg-slate-50">
                  <td className="px-4 py-2.5 font-medium text-slate-800">{lang.name}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-slate-500">{lang.code}</td>
                  <td className="px-4 py-2.5">
                    <span className={`rounded px-2 py-0.5 text-xs font-medium ${VOICE_COLORS[lang.stt] ?? 'bg-slate-100 text-slate-600'}`}>
                      {lang.stt}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs text-slate-500">{lang.stt_model}</td>
                  <td className="px-4 py-2.5">
                    <span className={`rounded px-2 py-0.5 text-xs font-medium ${VOICE_COLORS[lang.tts] ?? 'bg-slate-100 text-slate-600'}`}>
                      {lang.tts}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs text-slate-500">{lang.tts_model}</td>
                  <td className="px-4 py-2.5 text-slate-600">{lang.voice}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Usage Tab ────────────────────────────────────────────────────────────────

function UsageTab() {
  const [usage, setUsage] = useState<LLMUsage | null>(null);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);

  const load = useCallback((d: number) => {
    setLoading(true);
    getLLMUsage(d)
      .then(r => setUsage(r.data))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(days); }, [days, load]);

  const maxBar = (obj: Record<string, number>) => Math.max(...Object.values(obj), 1);

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <span className="text-sm text-slate-600">Range:</span>
        {[7, 14, 30].map(d => (
          <button
            key={d}
            onClick={() => setDays(d)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${days === d ? 'bg-indigo-600 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`}
          >
            {d}d
          </button>
        ))}
      </div>

      {loading ? <Spinner /> : usage && (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {[
              { label: 'Total Calls', value: usage.total_calls.toLocaleString(), color: 'text-slate-800' },
              { label: 'Successful', value: usage.success_calls.toLocaleString(), color: 'text-emerald-600' },
              { label: 'Errors', value: usage.error_calls.toLocaleString(), color: 'text-red-500' },
              { label: 'Error Rate', value: `${usage.error_rate}%`, color: usage.error_rate > 5 ? 'text-red-500' : 'text-slate-800' },
            ].map(card => (
              <div key={card.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <p className="text-xs text-slate-500 mb-1">{card.label}</p>
                <p className={`text-2xl font-bold ${card.color}`}>{card.value}</p>
              </div>
            ))}
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs text-slate-500 mb-1">Avg Latency</p>
            <p className="text-2xl font-bold text-slate-800">{usage.avg_duration_ms.toLocaleString()} ms</p>
          </div>

          {usage.total_calls === 0 && (
            <p className="text-center text-slate-400 py-8 text-sm">No LLM call logs found for this range. Logs are written to <code className="font-mono">logs/llm-calls/</code> when the AI agents service is running.</p>
          )}

          {usage.total_calls > 0 && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {/* By Provider */}
              <BarChart title="Calls by Provider" data={usage.by_provider} colorClass="bg-purple-400" max={maxBar(usage.by_provider)} />
              {/* By Call Type */}
              <BarChart title="Calls by Type" data={usage.by_call_type} colorClass="bg-blue-400" max={maxBar(usage.by_call_type)} />
              {/* By Status */}
              <BarChart
                title="Calls by Status"
                data={usage.by_status}
                colorFn={k => k === 'success' ? 'bg-emerald-400' : 'bg-red-400'}
                max={maxBar(usage.by_status)}
              />
              {/* By Date */}
              <BarChart title="Calls by Date" data={usage.by_date} colorClass="bg-indigo-400" max={maxBar(usage.by_date)} sortByKey />
            </div>
          )}
        </>
      )}
    </div>
  );
}

function BarChart({
  title, data, colorClass, colorFn, max, sortByKey = false,
}: {
  title: string;
  data: Record<string, number>;
  colorClass?: string;
  colorFn?: (key: string) => string;
  max: number;
  sortByKey?: boolean;
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <h4 className="text-sm font-semibold text-slate-700 mb-3">{title}</h4>
      <div className="space-y-2">
        {Object.entries(data)
          .sort((a, b) => sortByKey ? a[0].localeCompare(b[0]) : b[1] - a[1])
          .map(([k, v]) => (
          <div key={k} className="flex items-center gap-2">
            <span className="w-32 shrink-0 truncate text-xs text-slate-600">{k}</span>
            <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
              <div
                className={`h-2 rounded-full ${colorFn ? colorFn(k) : colorClass}`}
                style={{ width: `${(v / max) * 100}%` }}
              />
            </div>
            <span className="w-10 text-right text-xs font-medium text-slate-700">{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── System Prompts Tab ───────────────────────────────────────────────────────

function SystemPromptsTab() {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-10 text-center shadow-sm">
      <svg className="mx-auto mb-4 h-10 w-10 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
      </svg>
      <p className="text-sm font-medium text-slate-600">This section is not visible for admins yet.</p>
      <p className="mt-1 text-xs text-slate-400">System prompt management will be available in a future release.</p>
    </div>
  );
}

// ─── Shared ───────────────────────────────────────────────────────────────────

function Spinner() {
  return (
    <div className="flex items-center justify-center py-16">
      <div className="h-7 w-7 rounded-full border-2 border-indigo-200 border-t-indigo-600 animate-spin" />
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function LLMConfig() {
  const [tab, setTab] = useState<Tab>('Model Chains');

  return (
    <div>
      <h1 className="text-2xl font-semibold text-slate-800 mb-6">AI & LLM Configuration</h1>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-slate-200">
        {TABS.map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors ${
              tab === t
                ? 'border-b-2 border-indigo-600 text-indigo-600 bg-white -mb-px'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'Model Chains'   && <ChainsTab />}
      {tab === 'LLM Providers'  && <ProvidersTab />}
      {tab === 'Voice Config'   && <VoiceConfigTab />}
      {tab === 'Usage'          && <UsageTab />}
      {tab === 'System Prompts' && <SystemPromptsTab />}
    </div>
  );
}
